/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { ICartActionResult } from '@msdyn365-commerce/global-state';
import classnames from 'classnames';
import { computed, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ICittaCheckoutGuestProfileData } from './citta-checkout-guest-profile.data';
import { ICittaCheckoutGuestProfileProps } from './citta-checkout-guest-profile.props.autogenerated';
import getAddContactInfo, { IAddContactInfo } from './components/get-add-contact-info';
import getShowContactInfo, { IShowContactInfo } from './components/get-show-contact-info';

export * from './components/get-add-contact-info';
export * from './components/get-show-contact-info';

interface ICheckoutGuestProfileState {
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    companyName: string;
    hasEmailError: boolean;
    hasCompanyNameError: boolean;
    hasFirstNameError: boolean;
    hasLastNameError: boolean;
    hasPhoneNumberError: boolean;
}

export interface ICheckoutGuestProfileModuleProps extends ICittaCheckoutGuestProfileProps<ICittaCheckoutGuestProfileData>, IModuleStateProps { }

export interface ICheckoutGuestProfileViewProps extends ICittaCheckoutGuestProfileProps<ICittaCheckoutGuestProfileData>, ICheckoutGuestProfileState {
    moduleProps: IModuleProps;
    showContactInfo?: IShowContactInfo;
    addContactInfo?: IAddContactInfo;
    isB2B: Boolean


    onChange?(email: string): void;
    onEdit?(): void;
    onSubmit?(): void;
    onCancel?(): void;
}

/**
 *
 * CheckoutGuestProfile component
 * @extends {React.PureComponent<ICittaCheckoutGuestProfileProps<ICittaCheckoutGuestProfileData>, ICheckoutGuestProfileState>}
 */
// @ts-ignore
@withModuleState
@observer
class CheckoutGuestProfile extends React.Component<ICheckoutGuestProfileModuleProps, ICheckoutGuestProfileState> {
    public state: ICheckoutGuestProfileState = {
        email:
            (this.props.context &&
                this.props.context.request &&
                this.props.context.request.user &&
                this.props.context.request.user.isAuthenticated &&
                this.props.context.request.user.emailAddress) ||
            '',
        firstName:
            (this.props.context &&
                this.props.context.request &&
                this.props.context.request.user &&
                this.props.context.request.user.isAuthenticated &&
                this.props.context.request.user.firstName) || '',
        lastName:
            (this.props.context &&
                this.props.context.request &&
                this.props.context.request.user &&
                this.props.context.request.user.isAuthenticated &&
                this.props.context.request.user.lastName) || '',
        phoneNumber: '',
        companyName: '',
        hasEmailError: false,
        hasCompanyNameError: false,
        hasFirstNameError: false,
        hasLastNameError: false,
        hasPhoneNumberError: false,
    };

    private inputRef: React.RefObject<HTMLInputElement> = React.createRef();
    private inputFirstNameRef: React.RefObject<HTMLInputElement> = React.createRef();
    private inputLastNameRef: React.RefObject<HTMLInputElement> = React.createRef();
    private inputPhoneNumberRef: React.RefObject<HTMLInputElement> = React.createRef();
    private inputCompanyNameRef: React.RefObject<HTMLInputElement> = React.createRef();
    @computed get isDataReady(): boolean {
        return (this.props.data.checkout.result && this.props.data.checkout.status) === 'SUCCESS';
    }

    public componentDidMount(): void {
        // @ts-ignore: Compiler not recognizing condition check for function params
        when(
            () => this.isDataReady,
            async () => {
                await this.init();
            }
        );
    }

    public render(): JSX.Element {
        const {firstName, lastName, phoneNumber, companyName} = this.state;
        const {
            config: { className },
            resources,
            moduleState: { isReady, hasError, hasExternalSubmitGroup }
        } = this.props;
        const email = (this.props.data.checkout.result && this.props.data.checkout.result.guestCheckoutEmail) || '';
        const viewProps: ICheckoutGuestProfileViewProps = {
            ...this.props,
            ...this.state,
            onChange: this.onChange,
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            isB2B: this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b',

            moduleProps: {
                moduleProps: this.props,
                className: classnames('ms-checkout-guest-profile', className, isReady ? 'show' : 'add')
            },
            showContactInfo: isReady
                ? getShowContactInfo({
                    canEdit: !hasExternalSubmitGroup,
                    email,
                    firstName,
                    lastName,
                    phoneNumber,
                    companyName,
                    resources,
                    onEdit: this.onEdit
                })
                : undefined,
            addContactInfo: !isReady
                ? getAddContactInfo({
                    email: this.state.email,
                    firstName: firstName,
                    lastName: lastName,
                    companyName: companyName,
                    phoneNumber: phoneNumber,
                    hasError: hasError,
                    hasEmailError: this.state.hasEmailError,
                    hasCompanyNameError: this.state.hasCompanyNameError,
                    hasFirstNameError: this.state.hasFirstNameError,
                    hasLastNameError: this.state.hasLastNameError,
                    hasPhoneNumberError: this.state.hasPhoneNumberError,
                    resources,
                    inputRef: this.inputRef,
                    inputFirstNameRef: this.inputFirstNameRef,
                    inputLastNameRef: this.inputLastNameRef,
                    inputPhoneNumberRef: this.inputPhoneNumberRef,
                    inputCompanyNameRef: this.inputCompanyNameRef,
                    onChange: this.onChange,
                    canSubmit: !hasExternalSubmitGroup,
                    onSubmit: this.onSubmit,
                    canCancel: !hasExternalSubmitGroup && !!email,
                    onCancel: this.onCancel
                })
                : undefined
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    // tslint:disable-next-line:cyclomatic-complexity
    private init = async (): Promise<void> => {
        const hasInvoiceLine = this.props.data.checkout.result?.checkoutCart.hasInvoiceLine;
        const additionalProperties = this.getPreFilledProperties();
        const {email, firstName, lastName, phoneNumber } = additionalProperties;
        if (email) {
            await this.updateGuestProfile(email, additionalProperties);
        }
        const isEmailValid = this.isEmailValid(email);
        const isFirstNameValid = this.isNameValid(additionalProperties.firstName);
        const isLastNameValid = this.isNameValid(additionalProperties.lastName);
        const isPhoneNumberValid = this.isPhoneNumberValid(additionalProperties.phoneNumber);
        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            status: hasInvoiceLine ? 'disabled' : (firstName && isFirstNameValid && lastName && isLastNameValid && phoneNumber &&
                isPhoneNumberValid && email && isEmailValid ? 'ready' : 'updating'),
            hasError: !!email && !isEmailValid && !isFirstNameValid && !isLastNameValid && !isPhoneNumberValid
        });
    };

    private getPreFilledProperties = () => {
        const hasInvoiceLine = this.props.data.checkout.result?.checkoutCart.hasInvoiceLine;
        const emailOnCart = this.props.data.checkout.result?.checkoutCart.cart.ReceiptEmail;
        const isB2B = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';
        const isAuthenticated =
          this.props.context &&
          this.props.context.request &&
          this.props.context.request.user &&
          this.props.context.request.user.isAuthenticated || false;
        let email =  emailOnCart || '';
        let firstName = this.getExtensionPropertyValue('FIRSTNAME');
        let lastName = this.getExtensionPropertyValue('LASTNAME');
        let phoneNumber = this.getExtensionPropertyValue('PHONENUMBER');
        let companyName = this.getExtensionPropertyValue('COMPANYNAME');
        if(hasInvoiceLine || isAuthenticated) {
            email =  email || (this.props.context.request.user.emailAddress || '');
            firstName = isB2B? '' : firstName || (this.props.context.request.user.firstName?.toString() || '');
            lastName = isB2B? '' :  lastName || (this.props.context.request.user.lastName || '');
            phoneNumber = phoneNumber;
            companyName = isB2B? (this.props.context.request.user.firstName || '') :  companyName;
        } else {
            email = (emailOnCart || this.state.email);
            firstName = (firstName || this.state.firstName);
            lastName = (lastName || this.state.lastName);
            phoneNumber = (phoneNumber || this.state.phoneNumber);
            companyName = (companyName || this.state.companyName);
        }

        return {email, firstName, lastName, phoneNumber, companyName};
    }

    private getExtensionPropertyValue = (fieldName: string) => {
    const value = this.props.data.checkout.result?.checkoutCart.cart.ExtensionProperties &&
        this.props.data.checkout.result?.checkoutCart.cart.ExtensionProperties?.filter(p=>p.Key === fieldName);
    if (value && value?.length > 0) {
        return value[0].Value?.StringValue || '';
    }

    return '';
    }

    private onChange = (rowEmail: string, fName?: string, lName?: string, phone?: string, company?: string): void => {
        const email = (rowEmail || '').replace(new RegExp('[<>]', 'gi'), '');
        const fNameText = (fName || '').replace(new RegExp('[<>]', 'gi'), '');
        const lNameText = (lName || '').replace(new RegExp('[<>]', 'gi'), '');
        const phoneText = (phone || '').replace(new RegExp('[<>]', 'gi'), '');
        const companyText = (company || '').replace(new RegExp('[<>]', 'gi'), '');
        const isB2B = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';
        if(isB2B){
            this.setState({firstName:fNameText, lastName: lNameText, phoneNumber: phoneText});
        } else {
            this.setState({ email, firstName:fNameText, lastName: lNameText, phoneNumber: phoneText, companyName: companyText });
        }
        if (this.props.moduleState.hasError) {
            // Clear error during user updating the email
            this.props.moduleState.setHasError(false);
        }
    };

    private isEmailValid = (email: string): boolean => {
        const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(email);
    };

    private isPhoneNumberValid = (phoneNumber: string): boolean => {
        const regex = /^[0-9]+[0-9]*$/;
        return regex.test(phoneNumber);
    };

    private isNameValid = (name: string): boolean => {
        const regex = /^.+$/;
        return regex.test(name);
    };
    private isCompanyNameValid = (companyName : string): boolean => {
        const regex = /^.+$/;
        return regex.test(companyName);
    }

    private updateGuestProfile = async (newGuestCheckoutEmail: string, additionalProperties?: {}): Promise<void> => {
        if (this.props.data.checkout.result) {
            await this.props.data.checkout.result.updateGuestCheckoutEmail(
                { newGuestCheckoutEmail, additionalProperties });
            this.setState({ ...additionalProperties, email: newGuestCheckoutEmail });
        }
    };

    private onEdit = (): void => {
        this.props.moduleState.onUpdating();
    };

    // tslint:disable-next-line:cyclomatic-complexity
    private onSubmit = async (): Promise<void> => {
        const isB2B = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';
        const { email, firstName, lastName, phoneNumber, companyName } = this.state;
        const isValid = this.isEmailValid(email);
        const isFirstNameValid = isB2B? true : this.isNameValid(firstName);
        const isLastNameValid =  isB2B? true : this.isNameValid(lastName);
        const isPhoneNumberValid = this.isPhoneNumberValid(phoneNumber);
        const isCompanyNameValid = isB2B? this.isCompanyNameValid(companyName): true;
        this.setState(
            {
                hasEmailError: !isValid,
                hasFirstNameError: !isFirstNameValid,
                hasLastNameError: !isLastNameValid,
                hasPhoneNumberError: !isPhoneNumberValid,
                hasCompanyNameError: !isCompanyNameValid
            });
        if (!isValid || !isFirstNameValid || !isLastNameValid || !isPhoneNumberValid || !isCompanyNameValid) {
            if(!isFirstNameValid) {
                // Get error, focus back on the input field
                const inputFirstName = this.inputFirstNameRef && this.inputFirstNameRef.current && this.inputFirstNameRef.current.focus && (this.inputFirstNameRef.current as HTMLElement);
                inputFirstName && inputFirstName.focus();
            }
            if(!isLastNameValid) {
                // Get error, focus back on the input field
                const inputLastName = this.inputLastNameRef && this.inputLastNameRef.current && this.inputLastNameRef.current.focus && (this.inputLastNameRef.current as HTMLElement);
                inputLastName && inputLastName.focus();
            }
            if(!isPhoneNumberValid) {
                // Get error, focus back on the input field
                const inputPhoneNumber = this.inputPhoneNumberRef && this.inputPhoneNumberRef.current && this.inputPhoneNumberRef.current.focus && (this.inputPhoneNumberRef.current as HTMLElement);
                inputPhoneNumber && inputPhoneNumber.focus();
            }
            if(!isCompanyNameValid) {
                // Get error, focus back on the input field
                const inputCompanyName = this.inputCompanyNameRef && this.inputCompanyNameRef.current && this.inputCompanyNameRef.current.focus && (this.inputCompanyNameRef.current as HTMLElement);
                inputCompanyName && inputCompanyName.focus();
            } else {
                // Get error, focus back on the input field
                const input = this.inputRef && this.inputRef.current && this.inputRef.current.focus && (this.inputRef.current as HTMLElement);
                input && input.focus();
            }

            this.props.moduleState.setHasError(true);
        } else {
            this.props.moduleState.onReady();
            // Add extension properties for additional fields
            this._updateCheckoutCart().then((res) => {
                if(res?.status === 'SUCCESS') {
                    this.updateGuestProfile(email).catch(err=> {throw err;});
                }
            }).catch((error: Error) => {
                throw error;
            });
        }
    };


    private onCancel = (): void => {
        const additionalProperties = this.getPreFilledProperties();
        const { firstName, lastName, phoneNumber, companyName } = additionalProperties;
        // Reset to the saved value
        if (!this.props.data.checkout.result) {
            return;
        }

        const email = this.props.data.checkout.result.guestCheckoutEmail;
        if (!email || !firstName || !lastName || !phoneNumber) {
            // It has no saved email, set to status updating and request user to fill the email form
            this.props.moduleState.onUpdating();
            return;
        } else {
            // It has saved email, set to status ready
            this.setState({
                firstName: firstName,
                lastName: lastName,
                email: email,
                phoneNumber: phoneNumber,
                companyName: companyName
            });
            this.props.moduleState.setHasError(false);
            this.props.moduleState.onReady();
        }
    };

    private _updateCheckoutCart = async (): Promise<ICartActionResult | undefined> => {
       return this.props.data.checkout.result?.checkoutCart.updateExtensionProperties(
        {newExtensionProperties: [ { Key: 'FIRSTNAME', Value: { StringValue: this.state.firstName } },
        { Key: 'LASTNAME', Value: { StringValue: this.state.lastName } },
        { Key: 'EMAILADDRESS', Value: { StringValue: this.state.email } },
        { Key: 'PHONENUMBER', Value: { StringValue: this.state.phoneNumber } },
        { Key: 'COMPANYNAME', Value: { StringValue: this.state.companyName } }]});
    }
}

export default CheckoutGuestProfile;